/**
 * Database Logger Service
 * Logs database operations via LogManagement API
 * Focuses on admin CRUD operations with performance metrics
 */

import { apiService } from './api';
import { DatabaseLogDto, CreateDatabaseLogDto } from '../types/logManagement';
import { loggingHealthService } from './loggingHealthService';

export interface DatabaseOperation {
  type: 'CREATE' | 'READ' | 'UPDATE' | 'DELETE' | 'DDL' | 'TRANSACTION';
  table: string;
  recordId?: string | undefined;
  query?: string | undefined;
  parameters?: Record<string, any> | undefined;
  duration: number;
  rowsAffected?: number | undefined;
  success: boolean;
  error?: Error | undefined;
  connectionId?: string | undefined;
  databaseName?: string | undefined;
}

export interface DatabaseLogContext {
  userId?: string;
  sessionId?: string;
  source?: string;
  category?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  adminOperation?: boolean;
  transactionId?: string;
}

export class DatabaseLogger {
  private isInitialized = false;
  private batchQueue: DatabaseLogDto[] = [];
  private readonly batchSize = 15;
  private readonly batchTimeout = 8000; // 8 seconds
  private batchTimer?: NodeJS.Timeout;
  private isOnline = true;
  private activeTransactions = new Map<string, Date>();

  constructor() {
    this.setupNetworkListeners();
  }

  /**
   * Initialize the database logger
   */
  initialize(): void {
    if (this.isInitialized) return;

    console.log('DatabaseLogger: Initializing...');
    this.isInitialized = true;
    this.startBatchTimer();
    
    // Process any queued logs from offline mode
    this.processBatchQueue();
  }

  /**
   * Log a database operation with enhanced error handling and endpoint availability checks
   */
  async logDatabaseOperation(
    operation: DatabaseOperation,
    context?: DatabaseLogContext
  ): Promise<void> {
    try {
      // Check circuit breaker - skip logging if service is down
      if (this.isDatabaseCircuitBreakerOpen()) {
        console.warn('DatabaseLogger: Circuit breaker open, skipping database log to prevent further failures');
        return;
      }

      // Check endpoint availability before attempting to log
      const serviceHealth = loggingHealthService.getServiceHealth('databaseLogger');
      if (!serviceHealth.endpointAvailable && !serviceHealth.isHealthy) {
        console.warn('DatabaseLogger: Endpoint unavailable, storing for offline processing');
        const databaseLog = this.createDatabaseLog(operation, context);
        this.storeOfflineLog(databaseLog);
        return;
      }

      const databaseLog = this.createDatabaseLog(operation, context);
      
      if (this.isOnline) {
        // Log critical operations immediately (errors, admin operations, slow queries)
        if (this.isCriticalOperation(operation, context)) {
          await this.sendDatabaseLog(databaseLog);
        } else {
          // Add to batch queue for regular operations
          this.addToBatch(databaseLog);
        }
      } else {
        // Store offline for later processing
        this.storeOfflineLog(databaseLog);
      }
    } catch (error) {
      console.error('DatabaseLogger: Failed to log database operation:', error);
      // Don't throw - logging failures shouldn't break database operations
      // This ensures truly non-blocking behavior
    }
  }

  /**
   * Log admin CRUD operations
   */
  async logAdminCrudOperation(
    operation: 'CREATE' | 'READ' | 'UPDATE' | 'DELETE',
    table: string,
    recordId?: string,
    duration: number = 0,
    rowsAffected?: number,
    context?: DatabaseLogContext
  ): Promise<void> {
    await this.logDatabaseOperation({
      type: operation,
      table,
      recordId,
      duration,
      rowsAffected,
      success: true,
      databaseName: 'main'
    }, {
      ...context,
      adminOperation: true,
      source: context?.source || 'AdminService', // Use context source if provided, otherwise default to AdminService
      category: context?.category || 'admin_crud',
      tags: [...(context?.tags || []), 'admin', 'crud', operation.toLowerCase()]
    });
  }

  /**
   * Log user management operations
   */
  async logUserManagementOperation(
    operation: 'create_user' | 'update_user' | 'delete_user' | 'toggle_user_status',
    userId: string,
    duration: number,
    success: boolean,
    error?: Error,
    context?: DatabaseLogContext
  ): Promise<void> {
    await this.logDatabaseOperation({
      type: operation.includes('create') ? 'CREATE' : 
            operation.includes('update') || operation.includes('toggle') ? 'UPDATE' : 'DELETE',
      table: 'users',
      recordId: userId,
      duration,
      rowsAffected: success ? 1 : 0,
      success,
      error
    }, {
      ...context,
      adminOperation: true,
      source: 'UserManagement',
      category: 'user_management',
      tags: [...(context?.tags || []), 'user-management', operation]
    });
  }

  /**
   * Log content management operations
   */
  async logContentManagementOperation(
    operation: 'CREATE' | 'UPDATE' | 'DELETE',
    contentType: string,
    contentId: string,
    duration: number,
    success: boolean,
    error?: Error,
    context?: DatabaseLogContext
  ): Promise<void> {
    await this.logDatabaseOperation({
      type: operation,
      table: contentType,
      recordId: contentId,
      duration,
      rowsAffected: success ? 1 : 0,
      success,
      error
    }, {
      ...context,
      adminOperation: true,
      source: 'ContentManagement',
      category: 'content_management',
      tags: [...(context?.tags || []), 'content-management', contentType, operation.toLowerCase()]
    });
  }

  /**
   * Log query performance metrics
   */
  async logQueryPerformance(
    queryType: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE',
    table: string,
    duration: number,
    rowsAffected?: number,
    queryHash?: string,
    context?: DatabaseLogContext
  ): Promise<void> {
    await this.logDatabaseOperation({
      type: queryType === 'SELECT' ? 'READ' : 
            queryType === 'INSERT' ? 'CREATE' : 
            queryType === 'UPDATE' ? 'UPDATE' : 'DELETE',
      table,
      duration,
      rowsAffected,
      success: true
    }, {
      ...context,
      source: 'QueryPerformanceMonitor',
      category: 'query_performance',
      tags: [...(context?.tags || []), 'performance', queryType.toLowerCase()],
      metadata: {
        ...context?.metadata,
        queryHash,
        performanceCategory: this.categorizeQueryPerformance(duration)
      }
    });
  }

  /**
   * Log transaction operations
   */
  async logTransaction(
    operation: 'BEGIN' | 'COMMIT' | 'ROLLBACK',
    transactionId: string,
    duration?: number,
    context?: DatabaseLogContext
  ): Promise<void> {
    if (operation === 'BEGIN') {
      this.activeTransactions.set(transactionId, new Date());
    } else {
      const startTime = this.activeTransactions.get(transactionId);
      const transactionDuration = startTime ? Date.now() - startTime.getTime() : duration || 0;
      this.activeTransactions.delete(transactionId);

      await this.logDatabaseOperation({
        type: 'TRANSACTION',
        table: 'transaction',
        recordId: transactionId,
        duration: transactionDuration,
        success: operation === 'COMMIT'
      }, {
        ...context,
        transactionId,
        source: 'TransactionManager',
        category: 'transaction',
        tags: [...(context?.tags || []), 'transaction', operation.toLowerCase()]
      });
    }
  }

  /**
   * Log slow queries
   */
  async logSlowQuery(
    queryType: string,
    table: string,
    query: string,
    duration: number,
    parameters?: Record<string, any>,
    context?: DatabaseLogContext
  ): Promise<void> {
    await this.logDatabaseOperation({
      type: queryType as any,
      table,
      query: this.sanitizeQuery(query),
      parameters: this.sanitizeParameters(parameters),
      duration,
      success: true
    }, {
      ...context,
      source: 'SlowQueryMonitor',
      category: 'slow_query',
      tags: [...(context?.tags || []), 'slow-query', 'performance-issue'],
      metadata: {
        ...context?.metadata,
        slowQueryThreshold: 1000, // 1 second
        queryComplexity: this.assessQueryComplexity(query)
      }
    });
  }

  /**
   * Create database log DTO
   */
  private createDatabaseLog(
    operation: DatabaseOperation,
    context?: DatabaseLogContext
  ): DatabaseLogDto {
    const level = !operation.success ? 'error' : 
                  operation.duration > 5000 ? 'warning' : 'info';
    
    const dto: DatabaseLogDto = {
      id: this.generateLogId(),
      timestamp: new Date().toISOString(),
      level,
      message: `Database ${operation.type}: ${operation.table}${operation.recordId ? ` (${operation.recordId})` : ''}`,
      source: context?.source || 'DatabaseLogger',
      category: context?.category || 'database_operation',
      queryType: operation.type === 'CREATE' ? 'INSERT' : 
                  operation.type === 'READ' ? 'SELECT' : 
                  operation.type === 'UPDATE' ? 'UPDATE' : 
                  operation.type === 'DELETE' ? 'DELETE' : 
                  operation.type === 'DDL' ? 'DDL' : 'TRANSACTION',
      executionTime: operation.duration,
      tags: this.generateTags(operation, context),
      metadata: {
        operation: operation.type,
        table: operation.table,
        recordId: operation.recordId,
        parameters: this.sanitizeParameters(operation.parameters),
        success: operation.success,
        error: operation.error ? {
          message: operation.error.message,
          stack: operation.error.stack
        } : undefined,
        performance: {
          executionTime: operation.duration,
          rowsAffected: operation.rowsAffected,
          performanceCategory: this.categorizeQueryPerformance(operation.duration)
        },
        context: context?.metadata || {},
        adminOperation: context?.adminOperation || false,
        transactionId: context?.transactionId,
        url: window.location.href,
        ...context?.metadata
      }
    };

    // Only add optional fields if they have values
    const userId = context?.userId || this.getCurrentUserId();
    if (userId) dto.userId = userId;
    
    const clientIP = this.getClientIP();
    if (clientIP) dto.ipAddress = clientIP;
    
    if (operation.rowsAffected !== undefined) dto.rowsAffected = operation.rowsAffected;
    if (operation.query) {
      const queryHash = this.generateQueryHash(operation.query);
      if (queryHash) dto.queryHash = queryHash;
      
      const sanitizedQuery = this.sanitizeQuery(operation.query);
      if (sanitizedQuery) dto.queryText = sanitizedQuery;
    }
    if (operation.connectionId) dto.connectionId = operation.connectionId;
    if (operation.databaseName) dto.databaseName = operation.databaseName;
    if (operation.table) dto.tableName = operation.table;

    return dto;
  }

  /**
   * Send database log to LogManagement API with comprehensive error handling
   */
  private async sendDatabaseLog(databaseLog: DatabaseLogDto): Promise<void> {
    try {
      // Check circuit breaker before attempting to send
      if (this.isDatabaseCircuitBreakerOpen()) {
        console.warn('DatabaseLogger: Circuit breaker open, skipping database log to prevent further failures');
        return;
      }

      // Map to CreateDatabaseLogDto format matching swagger schema
      const apiPayload = this.mapToCreateDatabaseLogDto(databaseLog);

      await apiService.post('/api/LogManagement/database-logs', apiPayload);
      console.log('DatabaseLogger: Successfully logged database operation:', 
        `${databaseLog.queryType} ${databaseLog.tableName}`);
      
      // Record successful logging to reset circuit breaker
      this.recordSuccessfulDatabaseLogging();
    } catch (error) {
      // Comprehensive error handling - NEVER throw to prevent breaking main flow
      this.handleDatabaseLoggingError(error, databaseLog);
    }
  }

  /**
   * Handle database logging errors with comprehensive graceful degradation
   */
  private handleDatabaseLoggingError(error: any, databaseLog: DatabaseLogDto): void {
    const errorInfo = {
      operation: databaseLog.queryType,
      table: databaseLog.tableName,
      error: error?.message || 'Unknown error',
      statusCode: error?.response?.status,
      responseData: error?.response?.data,
      timestamp: new Date().toISOString(),
      url: error?.config?.url || '/api/LogManagement/database-logs'
    };

    // Enhanced console logging for debugging without throwing exceptions
    console.error('DatabaseLogger: Failed to send database log:', errorInfo);
    
    // Log detailed error information for debugging
    if (error?.response?.data) {
      console.error('DatabaseLogger: Backend response details:', error.response.data);
    }
    
    if (error?.config) {
      console.error('DatabaseLogger: Request configuration:', {
        method: error.config.method,
        url: error.config.url,
        headers: error.config.headers
      });
    }

    // Check if we should retry based on error type
    const shouldRetry = this.shouldRetryDatabaseLogging(error);
    
    if (shouldRetry) {
      // Store for retry if it's a recoverable error
      this.storeOfflineLog(databaseLog);
      console.log('DatabaseLogger: Stored database log for retry due to recoverable error');
    } else {
      // Log validation errors for debugging but don't retry
      console.warn('DatabaseLogger: Not retrying database log due to client error (likely validation issue)');
      this.storeFailedDatabaseLog(databaseLog, error);
    }

    // Check database logging service health
    this.checkDatabaseLoggingHealth(error);
    
    // Ensure main application flow continues regardless of logging failures
    // This is critical for non-blocking behavior
  }

  /**
   * Determine if database logging should be retried
   */
  private shouldRetryDatabaseLogging(error: any): boolean {
    const statusCode = error?.response?.status;
    
    // Don't retry validation errors (400)
    if (statusCode === 400) {
      return false;
    }
    
    // Don't retry authentication errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      return false;
    }
    
    // Retry server errors (500+) and network issues
    return statusCode >= 500 || !statusCode;
  }

  /**
   * Store failed database logs for debugging
   */
  private storeFailedDatabaseLog(databaseLog: DatabaseLogDto, error: any): void {
    try {
      const failedLogs = this.getFailedDatabaseLogs();
      failedLogs.push({
        log: databaseLog,
        error: {
          message: error?.message,
          statusCode: error?.response?.status,
          responseData: error?.response?.data
        },
        timestamp: new Date().toISOString()
      });

      // Keep only the most recent 25 failed logs
      if (failedLogs.length > 25) {
        failedLogs.splice(0, failedLogs.length - 25);
      }

      localStorage.setItem('failedDatabaseLogs', JSON.stringify(failedLogs));
    } catch (storageError) {
      console.error('DatabaseLogger: Failed to store failed database log:', storageError);
    }
  }

  /**
   * Get failed database logs from storage
   */
  private getFailedDatabaseLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem('failedDatabaseLogs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Record successful database logging to reset circuit breaker
   */
  private recordSuccessfulDatabaseLogging(): void {
    const healthKey = 'databaseLoggingHealth';
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      // Reset failure count on successful request
      if (healthData.failures > 0) {
        healthData.failures = Math.max(0, healthData.failures - 1);
        healthData.lastSuccess = Date.now();
        
        // Close circuit breaker if it was open and we have a success
        if (healthData.circuitOpen) {
          healthData.circuitOpen = false;
          console.log('DatabaseLogger: Circuit breaker closed after successful request');
        }
        
        localStorage.setItem(healthKey, JSON.stringify(healthData));
      }
    } catch (storageError) {
      console.error('DatabaseLogger: Failed to record successful database logging:', storageError);
    }
  }

  /**
   * Check database logging service health with enhanced circuit breaker
   */
  private checkDatabaseLoggingHealth(error: any): void {
    const healthKey = 'databaseLoggingHealth';
    const now = Date.now();
    
    try {
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      if (!healthData.failures) {
        healthData.failures = 0;
        healthData.lastFailure = now;
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
      }
      
      // Increment failure counts
      healthData.failures++;
      healthData.consecutiveFailures = (healthData.consecutiveFailures || 0) + 1;
      healthData.lastFailure = now;
      
      // Enhanced circuit breaker logic
      const shouldOpenCircuit = 
        healthData.consecutiveFailures >= 5 || // 5 consecutive failures
        (healthData.failures >= 10 && (now - (healthData.lastSuccess || 0)) > 60000); // 10 failures in 1 minute
      
      if (shouldOpenCircuit && !healthData.circuitOpen) {
        healthData.circuitOpen = true;
        healthData.circuitOpenedAt = now;
        console.warn('DatabaseLogger: Circuit breaker opened due to repeated failures', {
          consecutiveFailures: healthData.consecutiveFailures,
          totalFailures: healthData.failures,
          errorType: error?.response?.status || 'network'
        });
      }
      
      // Auto-reset circuit breaker after timeout with exponential backoff
      const backoffTime = Math.min(300000, 60000 * Math.pow(2, Math.floor(healthData.failures / 5))); // Max 5 minutes
      if (healthData.circuitOpen && (now - healthData.circuitOpenedAt) > backoffTime) {
        healthData.circuitOpen = false;
        healthData.consecutiveFailures = 0;
        console.log('DatabaseLogger: Circuit breaker reset after timeout', {
          backoffTime: backoffTime / 1000 + 's'
        });
      }
      
      localStorage.setItem(healthKey, JSON.stringify(healthData));
    } catch (storageError) {
      console.error('DatabaseLogger: Failed to update health data:', storageError);
      // Ensure main application flow continues even if health tracking fails
    }
  }

  /**
   * Check if circuit breaker is open
   */
  private isDatabaseCircuitBreakerOpen(): boolean {
    try {
      const healthData = JSON.parse(localStorage.getItem('databaseLoggingHealth') || '{}');
      return healthData.circuitOpen || false;
    } catch {
      return false;
    }
  }

  /**
   * Map DatabaseLogDto to CreateDatabaseLogDto format matching swagger schema
   */
  private mapToCreateDatabaseLogDto(databaseLog: DatabaseLogDto): CreateDatabaseLogDto {
    // Map query type to operation string
    const operationMap: Record<string, string> = {
      'SELECT': 'read',
      'INSERT': 'create',
      'UPDATE': 'update',
      'DELETE': 'delete',
      'DDL': 'ddl',
      'TRANSACTION': 'transaction'
    };

    // Build payload matching CreateDatabaseLogDto schema exactly with field length constraints
    const apiPayload: any = {
      // Required fields (must be present)
      operation: (operationMap[databaseLog.queryType] || databaseLog.queryType.toLowerCase()).substring(0, 50),
      tableName: (databaseLog.tableName || 'unknown_table').substring(0, 100),
      
      // Optional fields (only include if they have values)
      entityId: databaseLog.metadata?.recordId || undefined,
      userId: databaseLog.userId || undefined,
      oldValues: databaseLog.metadata?.previousValue !== undefined ? JSON.stringify(databaseLog.metadata.previousValue) : undefined,
      newValues: databaseLog.metadata?.newValue !== undefined ? JSON.stringify(databaseLog.metadata.newValue) : undefined,
      query: databaseLog.queryText || undefined,
      executionTimeMs: databaseLog.executionTime || 0,
      rowsAffected: databaseLog.rowsAffected || undefined,
      isSuccess: databaseLog.metadata?.success !== false,
      errorMessage: databaseLog.metadata?.error?.message || (databaseLog.level === 'error' ? databaseLog.message : undefined),
      traceId: databaseLog.traceId || undefined
    };

    // Remove undefined values to keep payload clean
    Object.keys(apiPayload).forEach(key => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });

    return apiPayload;
  }

  /**
   * Add log to batch queue
   */
  private addToBatch(databaseLog: DatabaseLogDto): void {
    this.batchQueue.push(databaseLog);
    
    if (this.batchQueue.length >= this.batchSize) {
      this.processBatchQueue();
    }
  }

  /**
   * Process batch queue with enhanced error handling
   */
  private async processBatchQueue(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    // Check circuit breaker before processing batch
    if (this.isDatabaseCircuitBreakerOpen()) {
      console.warn('DatabaseLogger: Circuit breaker open, skipping batch processing');
      return;
    }

    const batch = [...this.batchQueue];
    this.batchQueue = [];

    let successCount = 0;
    let failureCount = 0;

    // Process each item individually to avoid losing entire batch on single failure
    for (const databaseLog of batch) {
      try {
        await this.sendDatabaseLog(databaseLog);
        successCount++;
      } catch (error) {
        failureCount++;
        // Individual items handle their own error logging and storage
        // Don't re-queue here as sendDatabaseLog handles retry logic
      }
    }

    if (successCount > 0) {
      console.log(`DatabaseLogger: Successfully processed ${successCount} of ${batch.length} database logs in batch`);
    }
    
    if (failureCount > 0) {
      console.warn(`DatabaseLogger: Failed to process ${failureCount} of ${batch.length} database logs in batch`);
    }
  }

  /**
   * Start batch timer
   */
  private startBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = setInterval(() => {
      if (this.batchQueue.length > 0) {
        this.processBatchQueue();
      }
    }, this.batchTimeout);
  }

  /**
   * Store log offline for later processing
   */
  private storeOfflineLog(databaseLog: DatabaseLogDto): void {
    try {
      const offlineLogs = this.getOfflineLogs();
      offlineLogs.push(databaseLog);
      
      // Keep only the most recent 100 offline logs
      if (offlineLogs.length > 100) {
        offlineLogs.splice(0, offlineLogs.length - 100);
      }
      
      localStorage.setItem('offlineDatabaseLogs', JSON.stringify(offlineLogs));
    } catch (error) {
      console.error('DatabaseLogger: Failed to store offline log:', error);
    }
  }

  /**
   * Get offline logs from storage
   */
  private getOfflineLogs(): DatabaseLogDto[] {
    try {
      return JSON.parse(localStorage.getItem('offlineDatabaseLogs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Process offline logs when back online
   */
  private async processOfflineLogs(): Promise<void> {
    const offlineLogs = this.getOfflineLogs();
    if (offlineLogs.length === 0) return;

    console.log(`DatabaseLogger: Processing ${offlineLogs.length} offline database logs`);

    try {
      for (const log of offlineLogs) {
        await this.sendDatabaseLog(log);
      }
      
      // Clear offline storage after successful processing
      localStorage.removeItem('offlineDatabaseLogs');
      console.log('DatabaseLogger: Successfully processed all offline database logs');
    } catch (error) {
      console.error('DatabaseLogger: Failed to process offline logs:', error);
    }
  }

  /**
   * Setup network listeners
   */
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      console.log('DatabaseLogger: Network back online, processing offline logs');
      this.isOnline = true;
      this.processOfflineLogs();
    });

    window.addEventListener('offline', () => {
      console.log('DatabaseLogger: Network offline, switching to offline mode');
      this.isOnline = false;
    });

    this.isOnline = navigator.onLine;
  }

  /**
   * Check if operation is critical and should be logged immediately
   */
  private isCriticalOperation(operation: DatabaseOperation, context?: DatabaseLogContext): boolean {
    // Log errors immediately
    if (!operation.success) return true;
    
    // Log admin operations immediately
    if (context?.adminOperation) return true;
    
    // Log slow queries immediately (> 5 seconds)
    if (operation.duration > 5000) return true;
    
    // Log DELETE operations immediately
    if (operation.type === 'DELETE') return true;
    
    // Log DDL operations immediately
    if (operation.type === 'DDL') return true;
    
    return false;
  }

  /**
   * Generate tags for the database log
   */
  private generateTags(
    operation: DatabaseOperation,
    context?: DatabaseLogContext
  ): string[] {
    const tags = ['database-log', operation.type.toLowerCase(), operation.table];
    
    // Add success/error tags
    if (!operation.success) tags.push('error');
    else tags.push('success');
    
    // Add performance tags
    if (operation.duration > 5000) tags.push('slow-query');
    else if (operation.duration < 100) tags.push('fast-query');
    
    // Add operation-specific tags
    if (typeof operation.rowsAffected === 'number' && operation.rowsAffected > 100) tags.push('bulk-operation');
    if (context?.adminOperation) tags.push('admin-operation');
    if (context?.transactionId) tags.push('transaction');
    
    // Add context tags
    if (context?.tags) tags.push(...context.tags);
    if (context?.userId) tags.push(`user:${context.userId}`);
    
    return tags;
  }

  /**
   * Helper methods
   */
  private generateLogId(): string {
    return `db_log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private generateQueryHash(query?: string): string | undefined {
    if (!query) return undefined;
    
    // Simple hash function for query identification
    let hash = 0;
    for (let i = 0; i < query.length; i++) {
      const char = query.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  private sanitizeQuery(query?: string): string | undefined {
    if (!query) return undefined;
    
    // Remove sensitive data from queries
    return query
      .replace(/password\s*=\s*'[^']*'/gi, "password = '[REDACTED]'")
      .replace(/token\s*=\s*'[^']*'/gi, "token = '[REDACTED]'")
      .replace(/secret\s*=\s*'[^']*'/gi, "secret = '[REDACTED]'");
  }

  private sanitizeParameters(parameters?: Record<string, any>): Record<string, any> | undefined {
    if (!parameters) return undefined;
    
    const sanitized = { ...parameters };
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'auth'];
    
    for (const field of sensitiveFields) {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    }
    
    return sanitized;
  }

  private categorizeQueryPerformance(duration: number): string {
    if (duration < 100) return 'fast';
    if (duration < 1000) return 'normal';
    if (duration < 5000) return 'slow';
    return 'very-slow';
  }

  private assessQueryComplexity(query?: string): string {
    if (!query) return 'unknown';
    
    const lowerQuery = query.toLowerCase();
    let complexity = 0;
    
    // Count complexity indicators
    if (lowerQuery.includes('join')) complexity += 2;
    if (lowerQuery.includes('subquery') || (lowerQuery.match(/select/g) || []).length > 1) complexity += 3;
    if (lowerQuery.includes('group by')) complexity += 1;
    if (lowerQuery.includes('order by')) complexity += 1;
    if (lowerQuery.includes('having')) complexity += 2;
    
    if (complexity === 0) return 'simple';
    if (complexity <= 3) return 'moderate';
    if (complexity <= 6) return 'complex';
    return 'very-complex';
  }

  private getCurrentUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  private getClientIP(): string {
    // In a real application, you might get this from a service or header
    return 'client-ip';
  }

  /**
   * Reset circuit breaker manually
   */
  resetCircuitBreaker(): void {
    try {
      const healthData = {
        failures: 0,
        circuitOpen: false,
        lastReset: Date.now()
      };
      localStorage.setItem('databaseLoggingHealth', JSON.stringify(healthData));
      console.log('DatabaseLogger: Circuit breaker manually reset');
    } catch (error) {
      console.error('DatabaseLogger: Failed to reset circuit breaker:', error);
    }
  }

  /**
   * Get database logging health status
   */
  getHealthStatus(): {
    isHealthy: boolean;
    circuitOpen: boolean;
    failures: number;
    lastFailure?: number;
    batchQueueSize: number;
    offlineLogsCount: number;
    failedLogsCount: number;
    activeTransactions: number;
  } {
    try {
      const healthData = JSON.parse(localStorage.getItem('databaseLoggingHealth') || '{}');
      const offlineLogs = this.getOfflineLogs();
      const failedLogs = this.getFailedDatabaseLogs();
      
      return {
        isHealthy: !healthData.circuitOpen && (healthData.failures || 0) < 3,
        circuitOpen: healthData.circuitOpen || false,
        failures: healthData.failures || 0,
        lastFailure: healthData.lastFailure,
        batchQueueSize: this.batchQueue.length,
        offlineLogsCount: offlineLogs.length,
        failedLogsCount: failedLogs.length,
        activeTransactions: this.activeTransactions.size
      };
    } catch {
      return {
        isHealthy: true,
        circuitOpen: false,
        failures: 0,
        batchQueueSize: this.batchQueue.length,
        offlineLogsCount: 0,
        failedLogsCount: 0,
        activeTransactions: this.activeTransactions.size
      };
    }
  }

  /**
   * Get database logging statistics
   */
  getStatistics(): {
    batchQueueSize: number;
    offlineLogsCount: number;
    activeTransactions: number;
    isOnline: boolean;
  } {
    return {
      batchQueueSize: this.batchQueue.length,
      offlineLogsCount: this.getOfflineLogs().length,
      activeTransactions: this.activeTransactions.size,
      isOnline: this.isOnline
    };
  }

  /**
   * Force flush all pending logs
   */
  async flush(): Promise<void> {
    console.log('DatabaseLogger: Force flushing all pending logs');
    
    try {
      await this.processBatchQueue();
      await this.processOfflineLogs();
      console.log('DatabaseLogger: Successfully flushed all pending logs');
    } catch (error) {
      console.error('DatabaseLogger: Failed to flush logs:', error);
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    
    // Try to flush any remaining logs
    this.flush().catch(error => {
      console.error('DatabaseLogger: Failed to flush logs during cleanup:', error);
    });
  }
}

// Export singleton instance
export const databaseLogger = new DatabaseLogger();

export default DatabaseLogger;